import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { toast } from "react-toastify";
import ClientAutocomplete from "../../components/clientAutocomplete";
import AnalysisPageTopbar from "../../components/metrics/AnalysisPageTopbar";
import {
  MONO_FONT,
  PAGE_FONT,
  SectionHead,
  headCellSx,
  textCellSx,
  useMetricsUi,
} from "../../components/metrics/metricsUi";
import { fieldSx } from "../manage-catalog/constants";
import { isMobile } from "../../globalStyles/mobile";
import { orderChannelReviewService } from "../../utils/services/orderChannelReviewService";

const FIELD_HEIGHT = 36;

const fieldControlSx = {
  ...fieldSx,
  "& .MuiOutlinedInput-root": {
    ...fieldSx["& .MuiOutlinedInput-root"],
    height: FIELD_HEIGHT,
    fontFamily: PAGE_FONT,
    fontSize: 13,
    borderRadius: "8px",
  },
};

const FieldLabel = ({ children, colors }) => (
  <Typography
    sx={{
      fontSize: 11,
      fontWeight: 600,
      color: colors.textTertiary,
      letterSpacing: "0.03em",
      textTransform: "uppercase",
      mb: 0.5,
      lineHeight: 1,
    }}
  >
    {children}
  </Typography>
);

const ToolbarField = ({ label, colors, children }) => (
  <Box sx={{ display: "flex", flexDirection: "column" }}>
    <FieldLabel colors={colors}>{label}</FieldLabel>
    {children}
  </Box>
);

const todayKey = () => new Date().toISOString().slice(0, 10);
const addDays = (dateKey, days) => {
  const d = new Date(`${dateKey}T00:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};

const formatDate = (value) => (value ? String(value).slice(0, 10) : "--");

const errorMessage = (err) =>
  err?.response?.data?.message || err?.message || "Something went wrong.";

/** Past tense for the toast after each action. */
const ACTION_VERB = {
  approve: "approved",
  reject: "rejected",
  reset: "reset",
  reconcile: "restored",
};

/** Reconciliation states (utils/order-channel-rules.js RECONCILE_STATES). */
const RECONCILE_LABELS = {
  repairable: "Repairable",
  evidence_changed: "Evidence changed",
  rule_missing: "Rule removed",
  provider_changed: "Provider source changed",
  incomplete: "Incomplete record",
  aligned: "Aligned",
  not_applicable: "Not applicable",
};
const RECONCILE_COLORS = {
  repairable: "primary",
  evidence_changed: "warning",
  rule_missing: "error",
  provider_changed: "default",
  incomplete: "default",
};

/**
 * Orders restored per request during Repair All. Each chunk is one short
 * request (well inside any proxy timeout), and progress updates after each.
 * The server caps this at 10,000.
 */
const REPAIR_CHUNK_SIZE = 5000;

/** Sum two count maps key by key. */
const addCounts = (into = {}, from = {}) => {
  const out = { ...into };
  Object.entries(from || {}).forEach(([key, count]) => {
    out[key] = (out[key] || 0) + (count || 0);
  });
  return out;
};

/** "a: 1, b: 2" for the non-zero entries of a count map. */
const formatCounts = (counts, labels = {}) =>
  Object.entries(counts || {})
    .filter(([, count]) => count > 0)
    .map(([key, count]) => `${labels[key] || key}: ${count}`)
    .join(", ");

/**
 * One "Needs reconciliation" row. Only `repairable` rows can be selected for a
 * bulk restore; `evidence_changed` gets an explicit single-order override or a
 * reset; `rule_missing` can only be reset; `provider_changed` / `incomplete`
 * are shown for information and never written from here.
 */
const ReconcileRow = ({ row, ui, acting, selected, onToggle, onConfirm }) => {
  const state = row.reconcile_state;
  const repairable = state === "repairable";
  const actionSx = { textTransform: "none", fontSize: 11.5 };
  return (
    <TableRow hover>
      <TableCell padding="checkbox">
        <Checkbox size="small" checked={selected} onChange={onToggle} disabled={!repairable} />
      </TableCell>
      <TableCell sx={{ ...textCellSx(ui), fontFamily: MONO_FONT }}>{row.order_id}</TableCell>
      <TableCell sx={textCellSx(ui)}>{formatDate(row.order_date)}</TableCell>
      <TableCell sx={{ ...textCellSx(ui), fontFamily: MONO_FONT }}>
        {row.current_source_channel || "--"}
      </TableCell>
      <TableCell sx={{ ...textCellSx(ui), fontFamily: MONO_FONT }}>
        <Tooltip
          title={
            row.reviewed_by_email || row.reviewed_at
              ? `Approved${row.reviewed_by_email ? ` by ${row.reviewed_by_email}` : ""}${
                  row.reviewed_at ? ` on ${formatDate(row.reviewed_at)}` : ""
                } · rule ${row.rule || "--"}` +
                (row.reconciliation_count
                  ? ` · restored ${row.reconciliation_count} time(s) before`
                  : "")
              : ""
          }
        >
          <span>{row.approved_source_channel || "--"}</span>
        </Tooltip>
      </TableCell>
      <TableCell sx={textCellSx(ui)}>
        <Tooltip
          title={
            row.approved_evidence?.length
              ? `Evidence when approved: ${row.approved_evidence.join(", ")}`
              : ""
          }
        >
          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
            {(row.evidence || []).length ? (
              row.evidence.map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  size="small"
                  sx={{ fontSize: 10.5, height: 20, fontFamily: MONO_FONT }}
                />
              ))
            ) : (
              <span>--</span>
            )}
          </Box>
        </Tooltip>
      </TableCell>
      <TableCell sx={textCellSx(ui)}>
        <Tooltip title={row.reconcile_reason || ""}>
          <Chip
            size="small"
            label={RECONCILE_LABELS[state] || state || "--"}
            color={RECONCILE_COLORS[state] || "default"}
            variant={repairable ? "outlined" : "filled"}
            sx={{ fontSize: 10.5, height: 20 }}
          />
        </Tooltip>
      </TableCell>
      <TableCell align="right">
        <Box sx={{ display: "flex", gap: 0.75, justifyContent: "flex-end" }}>
          {repairable && (
            <Button
              size="small"
              variant="outlined"
              disabled={acting}
              onClick={() => onConfirm({ kind: "restore-one", row, count: 1 })}
              sx={actionSx}
            >
              Restore
            </Button>
          )}
          {state === "evidence_changed" && (
            <Button
              size="small"
              variant="outlined"
              color="warning"
              disabled={acting}
              onClick={() => onConfirm({ kind: "override-one", row, count: 1 })}
              sx={actionSx}
            >
              Restore anyway
            </Button>
          )}
          {(state === "evidence_changed" || state === "rule_missing") && (
            <Button
              size="small"
              variant="outlined"
              disabled={acting}
              onClick={() => onConfirm({ kind: "reset-one", row, count: 1 })}
              sx={actionSx}
            >
              Reset
            </Button>
          )}
          {(state === "provider_changed" || state === "incomplete") && (
            <Tooltip title={row.reconcile_reason || ""}>
              <Typography sx={{ ...textCellSx(ui), fontSize: 11.5 }}>No action</Typography>
            </Tooltip>
          )}
        </Box>
      </TableCell>
    </TableRow>
  );
};

/**
 * Order Channel Review — historical Pipe17-mediated Shopify orders only.
 *
 * Pipe17 reports TikTok Shop / Pac-Sun / Urban Outfitters sales as plain
 * `source_channel: "shopify"`; the real channel survives only in the order's
 * tags. This page finds those orders and asks a human to confirm each
 * reclassification. Nothing is written until Approve or Reject is pressed —
 * searching is a pure read.
 *
 * Deliberately NOT part of ingestion: newly arriving orders are not classified
 * by anything here. This is a review/backfill tool for orders already in the
 * database.
 *
 * "Needs reconciliation" lists APPROVED decisions whose source_channel no
 * longer matches (typically a provider resync re-sending "shopify"). Restoring
 * them is a repair, not a re-review: the target comes from each order's own
 * audit record, and only orders whose tags still support the decision are
 * bulk-repairable. Anything else is shown with its reason.
 */
const OrderChannelReview = () => {
  const ui = useMetricsUi();
  const { colors } = ui;
  const storedClientId = localStorage.getItem("client_id");

  const [selectedClientData, setSelectedClientData] = useState(null);
  const clientId = selectedClientData?._id || storedClientId || "";

  const [startDate, setStartDate] = useState(() => addDays(todayKey(), -89));
  const [endDate, setEndDate] = useState(() => todayKey());
  const [mode, setMode] = useState("pending");

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reloadNonce, setReloadNonce] = useState(0);

  const [selectedIds, setSelectedIds] = useState([]);
  const [acting, setActing] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [lastSummary, setLastSummary] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  // Live state of a chunked Repair All: { repaired, examined, skipped, total,
  // chunks, stopping }. Null when no repair is running.
  const [repairProgress, setRepairProgress] = useState(null);
  // Read between chunks; a ref so the running loop sees the click immediately.
  const stopRepairRef = useRef(false);

  const invalidRange = !startDate || !endDate || startDate > endDate;

  // Memoised so the derived row sets below keep a stable identity between
  // renders — `payload?.data || []` would allocate a new array every time.
  const rows = useMemo(() => payload?.data || [], [payload]);
  const total = payload?.total || 0;

  const isPending = mode === "pending";
  const isReconcile = mode === "reconcile";

  /**
   * Only non-conflicted candidates may be bulk-approved. A conflict has to be
   * resolved one order at a time, on purpose. In reconciliation only
   * `repairable` rows may be bulk-restored.
   */
  const approvableRows = useMemo(
    () =>
      rows.filter((row) =>
        isReconcile ? row.reconcile_state === "repairable" : row.review_state === "candidate",
      ),
    [rows, isReconcile],
  );
  const conflictRows = useMemo(
    () => rows.filter((row) => row.review_state === "conflict"),
    [rows],
  );

  const selectedRows = useMemo(
    () => rows.filter((row) => selectedIds.includes(row._id)),
    [rows, selectedIds],
  );
  const selectedApprovable = useMemo(
    () =>
      selectedRows.filter((row) =>
        isReconcile ? row.reconcile_state === "repairable" : row.review_state === "candidate",
      ),
    [selectedRows, isReconcile],
  );

  useEffect(() => {
    setPage(0);
    setSelectedIds([]);
  }, [clientId, startDate, endDate, mode]);

  useEffect(() => {
    if (invalidRange) {
      setPayload(null);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const data = await orderChannelReviewService.investigate({
          clientId: clientId || undefined,
          startDate,
          endDate,
          mode,
          skip: page * rowsPerPage,
          limit: rowsPerPage,
        });
        if (cancelled) return;
        setPayload(data);
      } catch (err) {
        if (cancelled) return;
        setPayload(null);
        setError(errorMessage(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [clientId, startDate, endDate, mode, page, rowsPerPage, invalidRange, reloadNonce]);

  const refresh = useCallback(() => setReloadNonce((n) => n + 1), []);

  const runDecision = useCallback(
    async (request) => {
      setActing(true);
      try {
        const summary = await orderChannelReviewService.decide({
          ...request,
          clientId: clientId || undefined,
          startDate,
          endDate,
        });
        setLastSummary(summary);
        const changed =
          summary.action === "reconcile"
            ? summary.repaired || 0
            : (summary.approved || 0) + (summary.rejected || 0) + (summary.reset || 0);
        const skipped =
          Object.values(summary.skipped || {}).reduce((sum, n) => sum + n, 0) || 0;
        toast.success(
          `${changed} order(s) ${ACTION_VERB[summary.action] || summary.action}` +
            (skipped ? `, ${skipped} skipped` : ""),
        );
        if (summary.capped) {
          toast.warn(
            summary.action === "reconcile"
              ? "More orders matched than one run can restore. Run Repair All again to continue."
              : "More orders matched than one run can action. Narrow the date range and run again.",
          );
        }
        setSelectedIds([]);
        refresh();
      } catch (err) {
        toast.error(errorMessage(err));
      } finally {
        setActing(false);
        setConfirm(null);
      }
    },
    [clientId, startDate, endDate, refresh],
  );

  /**
   * Repair All always previews first: a dry run over the whole queried set,
   * whose counts become the confirmation. Nothing is written until Confirm.
   */
  const previewRepairAll = useCallback(async () => {
    setActing(true);
    setPreviewing(true);
    try {
      const preview = await orderChannelReviewService.decide({
        action: "reconcile",
        scope: "query",
        dryRun: true,
        clientId: clientId || undefined,
        startDate,
        endDate,
      });
      setConfirm({ kind: "repair-all", preview, count: preview.repairable || 0 });
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setActing(false);
      setPreviewing(false);
    }
  }, [clientId, startDate, endDate]);

  /**
   * Repair All, one chunk per request. The server returns `next_after_id` and
   * `done`; we loop until done, the user presses Stop, or a request fails.
   * Every chunk shares one `run_id`, so the whole run is one audit trail.
   *
   * Stopping or failing midway is safe: every restore is guarded and
   * idempotent, so running Repair All again simply picks up what is left.
   */
  const runRepairAll = useCallback(
    async (preview) => {
      const total = preview?.repairable || 0;
      stopRepairRef.current = false;
      setActing(true);
      let aggregate = {
        action: "reconcile",
        scope: "query",
        rule_version: preview?.rule_version,
        run_id: null,
        examined: 0,
        repaired: 0,
        by_target: {},
        by_state: {},
        skipped: {},
        chunks: 0,
      };
      setRepairProgress({ repaired: 0, examined: 0, skipped: 0, total, chunks: 0, stopping: false });

      let afterId = null;
      let failure = null;
      let stopped = false;
      try {
        for (;;) {
          const chunk = await orderChannelReviewService.decide({
            action: "reconcile",
            scope: "query",
            chunkSize: REPAIR_CHUNK_SIZE,
            afterId,
            runId: aggregate.run_id,
            clientId: clientId || undefined,
            startDate,
            endDate,
          });
          aggregate = {
            ...aggregate,
            rule_version: chunk.rule_version,
            run_id: chunk.run_id,
            examined: aggregate.examined + (chunk.examined || 0),
            repaired: aggregate.repaired + (chunk.repaired || 0),
            by_target: addCounts(aggregate.by_target, chunk.by_target),
            by_state: addCounts(aggregate.by_state, chunk.by_state),
            skipped: addCounts(aggregate.skipped, chunk.skipped),
            chunks: aggregate.chunks + 1,
          };
          const snapshot = {
            repaired: aggregate.repaired,
            examined: aggregate.examined,
            skipped: Object.values(aggregate.skipped).reduce((sum, n) => sum + n, 0),
            chunks: aggregate.chunks,
          };
          setRepairProgress((prev) => ({ ...prev, ...snapshot }));
          if (chunk.done || !chunk.next_after_id) break;
          if (stopRepairRef.current) {
            stopped = true;
            break;
          }
          afterId = chunk.next_after_id;
        }
      } catch (err) {
        failure = err;
      }

      setLastSummary(aggregate);
      if (failure) {
        toast.error(
          `${errorMessage(failure)} — ${aggregate.repaired} order(s) were restored before the error. ` +
            "Running Repair All again safely continues with the rest.",
        );
      } else if (stopped) {
        toast.warn(
          `Stopped after restoring ${aggregate.repaired} order(s). Run Repair All again to continue.`,
        );
      } else {
        const skipped = Object.values(aggregate.skipped).reduce((sum, n) => sum + n, 0);
        toast.success(
          `${aggregate.repaired} order(s) restored` + (skipped ? `, ${skipped} skipped` : ""),
        );
      }
      setRepairProgress(null);
      setActing(false);
      setConfirm(null);
      setSelectedIds([]);
      refresh();
    },
    [clientId, startDate, endDate, refresh],
  );

  const repairPercent =
    repairProgress && repairProgress.total
      ? Math.min(100, Math.round((repairProgress.repaired / repairProgress.total) * 100))
      : 0;

  const toggleRow = (id) =>
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((value) => value !== id) : [...prev, id],
    );

  const toggleAllOnPage = () => {
    const pageIds = approvableRows.map((row) => row._id);
    const allSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id));
    setSelectedIds(allSelected ? [] : pageIds);
  };

  const headerCheckboxState = useMemo(() => {
    const pageIds = approvableRows.map((row) => row._id);
    const picked = pageIds.filter((id) => selectedIds.includes(id));
    return {
      checked: pageIds.length > 0 && picked.length === pageIds.length,
      indeterminate: picked.length > 0 && picked.length < pageIds.length,
    };
  }, [approvableRows, selectedIds]);

  return (
    <Box
      sx={{
        backgroundColor: ui.darkMode ? colors.bgPrimary : "#F5F6F8",
        minHeight: "100%",
        fontFamily: PAGE_FONT,
      }}
    >
      <Box
        sx={{
          maxWidth: 1480,
          mx: "auto",
          padding: isMobile() ? "14px 12px 40px" : "22px 28px 56px",
        }}
      >
        <AnalysisPageTopbar
          badge="OCR"
          title="Order Channel Review"
          clientName={selectedClientData?.business_name || selectedClientData?.name}
          right={
            <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 1.25 }}>
              {!storedClientId && (
                <ToolbarField label="Client" colors={colors}>
                  <ClientAutocomplete
                    width={200}
                    selectedClient={selectedClientData}
                    onClientChange={(client) => setSelectedClientData(client)}
                    hideLabel
                  />
                </ToolbarField>
              )}
              <ToolbarField label="From" colors={colors}>
                <TextField
                  type="date"
                  size="small"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  error={invalidRange}
                  sx={{ ...fieldControlSx, width: 152 }}
                />
              </ToolbarField>
              <ToolbarField label="To" colors={colors}>
                <TextField
                  type="date"
                  size="small"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  error={invalidRange}
                  sx={{ ...fieldControlSx, width: 152 }}
                />
              </ToolbarField>
              <ToolbarField label="View" colors={colors}>
                <Box sx={{ display: "flex", gap: 0.75 }}>
                  <Button
                    size="small"
                    variant={isPending ? "contained" : "outlined"}
                    onClick={() => setMode("pending")}
                    sx={{ textTransform: "none", height: FIELD_HEIGHT, fontSize: 12 }}
                  >
                    Candidates
                  </Button>
                  <Button
                    size="small"
                    variant={isReconcile ? "contained" : "outlined"}
                    onClick={() => setMode("reconcile")}
                    sx={{ textTransform: "none", height: FIELD_HEIGHT, fontSize: 12 }}
                  >
                    Needs reconciliation
                  </Button>
                  <Button
                    size="small"
                    variant={mode === "reviewed" ? "contained" : "outlined"}
                    onClick={() => setMode("reviewed")}
                    sx={{ textTransform: "none", height: FIELD_HEIGHT, fontSize: 12 }}
                  >
                    Reviewed
                  </Button>
                </Box>
              </ToolbarField>
              <Button
                size="small"
                variant="outlined"
                startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
                onClick={refresh}
                disabled={loading || invalidRange}
                sx={{ textTransform: "none", height: FIELD_HEIGHT, fontSize: 12 }}
              >
                Search
              </Button>
            </Box>
          }
        />

        <Alert
          severity="info"
          icon={<InfoOutlinedIcon fontSize="inherit" />}
          sx={{ mb: 2, fontSize: 12.5, ...ui.card }}
        >
          Existing orders only. Scoped to Pipe17-mediated Shopify orders
          (<code>source_channel = &quot;shopify&quot;</code>) that carry a recognized
          channel tag. Direct Shopify orders are never included, and nothing is
          written until you approve or reject.
          {isReconcile &&
            " Needs reconciliation lists approved decisions whose source_channel was later overwritten (e.g. by a provider resync). Repairing restores the value you already approved — it is not a new review."}
          {payload?.rule_version ? ` Ruleset ${payload.rule_version}.` : ""}
        </Alert>

        {payload?.rules?.some((rule) => !rule.confirmed) && (
          <Alert severity="warning" sx={{ mb: 2, fontSize: 12.5, ...ui.card }}>
            Unconfirmed rule(s):{" "}
            {payload.rules
              .filter((rule) => !rule.confirmed)
              .map((rule) => `${rule.label} → ${rule.target}`)
              .join(", ")}
            . The exact tag format has not been verified against live data — review
            these suggestions especially carefully.
          </Alert>
        )}

        {invalidRange && (
          <Alert severity="warning" sx={{ mb: 2, fontSize: 12.5 }}>
            Select a valid From/To date range.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2, fontSize: 12.5 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ ...ui.card, p: 2 }}>
          <SectionHead
            title={
              isPending
                ? "Reclassification Candidates"
                : isReconcile
                  ? "Needs Reconciliation"
                  : "Reviewed Orders"
            }
            subtitle={
              isPending
                ? `${total} order(s) in range with recognized channel evidence and no existing review.`
                : isReconcile
                  ? `${total} approved order(s) in range whose source_channel no longer matches the approved classification.`
                  : `${total} order(s) already approved or rejected in this range.`
            }
            right={
              isReconcile ? (
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={acting || !selectedApprovable.length}
                    onClick={() =>
                      setConfirm({ kind: "repair-selected", count: selectedApprovable.length })
                    }
                    sx={{ textTransform: "none", fontSize: 12 }}
                  >
                    Repair Selected ({selectedApprovable.length})
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    disabled={acting || !total}
                    onClick={previewRepairAll}
                    sx={{ textTransform: "none", fontSize: 12 }}
                  >
                    {previewing ? "Counting…" : "Repair All…"}
                  </Button>
                </Box>
              ) : isPending ? (
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={acting || !selectedApprovable.length}
                    onClick={() =>
                      setConfirm({
                        kind: "approve-selected",
                        count: selectedApprovable.length,
                      })
                    }
                    sx={{ textTransform: "none", fontSize: 12 }}
                  >
                    Approve Selected ({selectedApprovable.length})
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="warning"
                    disabled={acting || !selectedRows.length}
                    onClick={() =>
                      setConfirm({ kind: "reject-selected", count: selectedRows.length })
                    }
                    sx={{ textTransform: "none", fontSize: 12 }}
                  >
                    Reject Selected ({selectedRows.length})
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    disabled={acting || !total}
                    onClick={() => setConfirm({ kind: "approve-all", count: total })}
                    sx={{ textTransform: "none", fontSize: 12 }}
                  >
                    Approve All
                  </Button>
                </Box>
              ) : null
            }
          />

          {conflictRows.length > 0 && isPending && (
            <Alert severity="warning" sx={{ my: 1.5, fontSize: 12.5 }}>
              {conflictRows.length} order(s) on this page match more than one channel.
              They are excluded from every bulk approval and must be resolved one at a
              time.
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress size={28} />
            </Box>
          ) : !rows.length ? (
            <Typography sx={{ ...textCellSx(ui), py: 4, textAlign: "center" }}>
              {invalidRange
                ? "Select a date range to begin."
                : isPending
                  ? "No candidates in this range. Every matching order is either already reviewed or carries no recognized channel tag."
                  : isReconcile
                    ? "Nothing to reconcile. Every approved classification in this range is still reflected in source_channel."
                    : "No reviewed orders in this range."}
            </Typography>
          ) : (
            <>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {(isPending || isReconcile) && (
                      <TableCell padding="checkbox" sx={headCellSx(ui)}>
                        <Checkbox
                          size="small"
                          checked={headerCheckboxState.checked}
                          indeterminate={headerCheckboxState.indeterminate}
                          onChange={toggleAllOnPage}
                          disabled={!approvableRows.length}
                        />
                      </TableCell>
                    )}
                    <TableCell sx={headCellSx(ui)}>Order</TableCell>
                    <TableCell sx={headCellSx(ui)}>Order Date</TableCell>
                    <TableCell sx={headCellSx(ui)}>Current Source</TableCell>
                    <TableCell sx={headCellSx(ui)}>
                      {isReconcile ? "Approved Source" : "Suggested Source"}
                    </TableCell>
                    <TableCell sx={headCellSx(ui)}>
                      {isReconcile ? "Current Evidence" : "Evidence"}
                    </TableCell>
                    <TableCell sx={headCellSx(ui)}>Status</TableCell>
                    <TableCell sx={headCellSx(ui)} align="right">
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => {
                    const conflicted = row.review_state === "conflict";
                    if (isReconcile) {
                      return (
                        <ReconcileRow
                          key={row._id}
                          row={row}
                          ui={ui}
                          acting={acting}
                          selected={selectedIds.includes(row._id)}
                          onToggle={() => toggleRow(row._id)}
                          onConfirm={setConfirm}
                        />
                      );
                    }
                    return (
                      <TableRow key={row._id} hover>
                        {isPending && (
                          <TableCell padding="checkbox">
                            <Checkbox
                              size="small"
                              checked={selectedIds.includes(row._id)}
                              onChange={() => toggleRow(row._id)}
                            />
                          </TableCell>
                        )}
                        <TableCell sx={{ ...textCellSx(ui), fontFamily: MONO_FONT }}>
                          {row.order_id}
                        </TableCell>
                        <TableCell sx={textCellSx(ui)}>{formatDate(row.order_date)}</TableCell>
                        <TableCell sx={{ ...textCellSx(ui), fontFamily: MONO_FONT }}>
                          {row.current_source_channel || "--"}
                        </TableCell>
                        <TableCell sx={{ ...textCellSx(ui), fontFamily: MONO_FONT }}>
                          {conflicted ? (
                            <Tooltip title={row.warning || ""}>
                              <Box
                                sx={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                  color: ui.warning,
                                }}
                              >
                                <WarningAmberOutlinedIcon sx={{ fontSize: 15 }} />
                                {(row.conflict || []).join(" / ")}
                              </Box>
                            </Tooltip>
                          ) : (
                            row.suggested_source_channel || "--"
                          )}
                        </TableCell>
                        <TableCell sx={textCellSx(ui)}>
                          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                            {(row.evidence || []).map((tag) => (
                              <Chip
                                key={tag}
                                label={tag}
                                size="small"
                                sx={{ fontSize: 10.5, height: 20, fontFamily: MONO_FONT }}
                              />
                            ))}
                          </Box>
                        </TableCell>
                        <TableCell sx={textCellSx(ui)}>
                          <Chip
                            size="small"
                            label={row.review_state || "--"}
                            color={
                              row.review_state === "approved"
                                ? "success"
                                : row.review_state === "rejected"
                                  ? "default"
                                  : conflicted
                                    ? "warning"
                                    : "primary"
                            }
                            variant={conflicted ? "filled" : "outlined"}
                            sx={{ fontSize: 10.5, height: 20 }}
                          />
                          {row.warning && !conflicted && (
                            <Tooltip title={row.warning}>
                              <WarningAmberOutlinedIcon
                                sx={{ fontSize: 15, ml: 0.5, color: ui.warning, verticalAlign: "middle" }}
                              />
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {isPending ? (
                            <Box sx={{ display: "flex", gap: 0.75, justifyContent: "flex-end" }}>
                              <Tooltip
                                title={
                                  conflicted
                                    ? "Resolve the conflict by picking one channel"
                                    : ""
                                }
                              >
                                <span>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    disabled={acting || conflicted}
                                    onClick={() =>
                                      setConfirm({ kind: "approve-one", row, count: 1 })
                                    }
                                    sx={{ textTransform: "none", fontSize: 11.5 }}
                                  >
                                    Approve
                                  </Button>
                                </span>
                              </Tooltip>
                              <Button
                                size="small"
                                variant="outlined"
                                color="warning"
                                disabled={acting}
                                onClick={() =>
                                  setConfirm({ kind: "reject-one", row, count: 1 })
                                }
                                sx={{ textTransform: "none", fontSize: 11.5 }}
                              >
                                Reject
                              </Button>
                            </Box>
                          ) : (
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={acting}
                              onClick={() => setConfirm({ kind: "reset-one", row, count: 1 })}
                              sx={{ textTransform: "none", fontSize: 11.5 }}
                            >
                              Reset
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={(_, next) => setPage(next)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(Number(e.target.value));
                  setPage(0);
                }}
                rowsPerPageOptions={[25, 50, 100]}
              />
            </>
          )}
        </Box>

        {lastSummary && lastSummary.action === "reconcile" && (
          <Box sx={{ ...ui.card, p: 2, mt: 2 }}>
            <SectionHead
              title="Last Action"
              subtitle={
                `reconcile (${lastSummary.scope}) · ruleset ${lastSummary.rule_version}` +
                (lastSummary.run_id ? ` · run ${lastSummary.run_id}` : "")
              }
            />
            <Typography sx={{ ...textCellSx(ui), mt: 1 }}>
              examined {lastSummary.examined || 0} · restored {lastSummary.repaired || 0}
              {formatCounts(lastSummary.by_target) &&
                ` (${formatCounts(lastSummary.by_target)})`}{" "}
              · skipped {formatCounts(lastSummary.skipped) || "none"}
            </Typography>
            {formatCounts(lastSummary.by_state, RECONCILE_LABELS) && (
              <Typography sx={{ ...textCellSx(ui), mt: 0.5 }}>
                by state: {formatCounts(lastSummary.by_state, RECONCILE_LABELS)}
              </Typography>
            )}
          </Box>
        )}

        {lastSummary && lastSummary.action !== "reconcile" && (
          <Box sx={{ ...ui.card, p: 2, mt: 2 }}>
            <SectionHead
              title="Last Action"
              subtitle={`${lastSummary.action} · ruleset ${lastSummary.rule_version}`}
            />
            <Typography sx={{ ...textCellSx(ui), mt: 1 }}>
              matched {lastSummary.matched} · approved {lastSummary.approved || 0} ·
              rejected {lastSummary.rejected || 0} · reset {lastSummary.reset || 0} ·
              skipped{" "}
              {Object.entries(lastSummary.skipped || {})
                .filter(([, count]) => count > 0)
                .map(([reason, count]) => `${reason}: ${count}`)
                .join(", ") || "none"}
            </Typography>
          </Box>
        )}

        {/* Explicit confirmation before ANY write. Approve All names the exact
            count it will act on, and says plainly that it covers the whole
            queried set rather than just the visible page. */}
        <Dialog
          open={Boolean(confirm)}
          onClose={() => !acting && !repairProgress && setConfirm(null)}
        >
          <DialogTitle sx={{ fontSize: 16, fontFamily: PAGE_FONT }}>
            {confirm?.kind?.startsWith("approve")
              ? "Approve reclassification?"
              : confirm?.kind?.startsWith("reject")
                ? "Reject suggestion?"
                : confirm?.kind?.startsWith("repair") || confirm?.kind === "restore-one"
                  ? "Restore approved classification?"
                  : confirm?.kind === "override-one"
                    ? "Restore despite changed evidence?"
                    : "Reset review?"}
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ fontSize: 13.5 }}>
              {confirm?.kind === "approve-all" && (
                <>
                  This will rewrite <strong>source_channel</strong> on{" "}
                  <strong>{confirm.count}</strong> order(s) — the entire queried set
                  for {startDate} to {endDate}, not just this page. Orders with
                  conflicting channel signals are skipped. The original value is
                  preserved on every order and analytics dashboards will pick the new
                  value up automatically.
                </>
              )}
              {confirm?.kind === "approve-selected" && (
                <>
                  This will rewrite <strong>source_channel</strong> on{" "}
                  <strong>{confirm.count}</strong> selected order(s). The original
                  value is preserved on each one.
                </>
              )}
              {confirm?.kind === "approve-one" && (
                <>
                  Change <strong>{confirm.row.order_id}</strong> from{" "}
                  <strong>{confirm.row.current_source_channel}</strong> to{" "}
                  <strong>{confirm.row.suggested_source_channel}</strong>? The original
                  value is preserved.
                </>
              )}
              {confirm?.kind === "reject-selected" && (
                <>
                  Record a rejection for <strong>{confirm.count}</strong> order(s).
                  <strong> source_channel is left unchanged</strong>, and these orders
                  will not appear in future searches unless reset.
                </>
              )}
              {confirm?.kind === "reject-one" && (
                <>
                  Record a rejection for <strong>{confirm.row.order_id}</strong>.
                  <strong> source_channel is left unchanged</strong>, and it will not
                  appear again unless reset.
                </>
              )}
              {confirm?.kind === "repair-all" && (
                <>
                  <strong>{confirm.count}</strong> order(s) in {startDate} to {endDate}{" "}
                  will have <strong>source_channel</strong> restored to the classification
                  already approved for them
                  {formatCounts(confirm.preview?.by_target)
                    ? ` (${formatCounts(confirm.preview.by_target)})`
                    : ""}
                  . Each restore is recorded on the order; the original decision is not
                  changed.
                  {(confirm.preview?.examined || 0) > (confirm.count || 0) && (
                    <>
                      {" "}
                      {(confirm.preview.examined || 0) - (confirm.count || 0)} other
                      order(s) will be left alone because they need individual attention (
                      {formatCounts(
                        { ...confirm.preview.by_state, repairable: 0 },
                        RECONCILE_LABELS,
                      )}
                      ).
                    </>
                  )}
                  {confirm.preview?.capped &&
                    " More orders matched than one run can restore — run it again afterwards."}
                </>
              )}
              {confirm?.kind === "repair-selected" && (
                <>
                  Restore the approved classification on <strong>{confirm.count}</strong>{" "}
                  selected order(s). Each restore is recorded on the order.
                </>
              )}
              {confirm?.kind === "restore-one" && (
                <>
                  Restore <strong>{confirm.row.order_id}</strong> from{" "}
                  <strong>{confirm.row.current_source_channel}</strong> to the approved{" "}
                  <strong>{confirm.row.approved_source_channel}</strong>?
                </>
              )}
              {confirm?.kind === "override-one" && (
                <>
                  <strong>{confirm.row.order_id}</strong>: {confirm.row.reconcile_reason}{" "}
                  Restore it to the approved{" "}
                  <strong>{confirm.row.approved_source_channel}</strong> anyway? This is
                  recorded as an override. To decide again instead, use Reset.
                </>
              )}
              {confirm?.kind === "reset-one" && (
                <>
                  Clear the review record on <strong>{confirm.row.order_id}</strong>.
                  If it was approved, <strong>source_channel</strong> is restored to{" "}
                  <strong>
                    {confirm.row.channel_review?.original_source_channel || "shopify"}
                  </strong>
                  . It will be offered for review again.
                </>
              )}
            </DialogContentText>
            {repairProgress && (
              <Box sx={{ mt: 2 }}>
                <LinearProgress
                  variant={repairProgress.total ? "determinate" : "indeterminate"}
                  value={repairPercent}
                  sx={{ height: 8, borderRadius: 4 }}
                />
                <Typography sx={{ fontSize: 12.5, mt: 1, fontFamily: PAGE_FONT }}>
                  Restored <strong>{repairProgress.repaired}</strong>
                  {repairProgress.total ? ` of ~${repairProgress.total}` : ""} order(s)
                  {repairProgress.total ? ` (${repairPercent}%)` : ""} · examined{" "}
                  {repairProgress.examined}
                  {repairProgress.skipped ? ` · skipped ${repairProgress.skipped}` : ""} · batch{" "}
                  {repairProgress.chunks + 1}
                </Typography>
                <Typography sx={{ fontSize: 11.5, mt: 0.5, color: "text.secondary" }}>
                  {repairProgress.stopping
                    ? "Stopping after the current batch…"
                    : "You can stop at any time; running Repair All again continues where it left off."}
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            {repairProgress ? (
              <Button
                color="warning"
                disabled={repairProgress.stopping}
                onClick={() => {
                  stopRepairRef.current = true;
                  setRepairProgress((prev) => ({ ...prev, stopping: true }));
                }}
                sx={{ textTransform: "none" }}
              >
                {repairProgress.stopping ? "Stopping…" : "Stop"}
              </Button>
            ) : (
              <Button
                onClick={() => setConfirm(null)}
                disabled={acting}
                sx={{ textTransform: "none" }}
              >
                Cancel
              </Button>
            )}
            <Button
              variant="contained"
              disabled={acting || (confirm?.kind === "repair-all" && !confirm.count)}
              onClick={() => {
                if (!confirm) return;
                if (confirm.kind === "approve-all") {
                  runDecision({ action: "approve", scope: "query" });
                } else if (confirm.kind === "approve-selected") {
                  runDecision({
                    action: "approve",
                    scope: "selected",
                    orderIds: selectedApprovable.map((row) => row._id),
                  });
                } else if (confirm.kind === "reject-selected") {
                  runDecision({
                    action: "reject",
                    scope: "selected",
                    orderIds: selectedRows.map((row) => row._id),
                  });
                } else if (confirm.kind === "approve-one") {
                  runDecision({
                    action: "approve",
                    scope: "selected",
                    orderIds: [confirm.row._id],
                  });
                } else if (confirm.kind === "reject-one") {
                  runDecision({
                    action: "reject",
                    scope: "selected",
                    orderIds: [confirm.row._id],
                  });
                } else if (confirm.kind === "repair-all") {
                  runRepairAll(confirm.preview);
                } else if (confirm.kind === "repair-selected") {
                  runDecision({
                    action: "reconcile",
                    scope: "selected",
                    orderIds: selectedApprovable.map((row) => row._id),
                  });
                } else if (confirm.kind === "restore-one") {
                  runDecision({
                    action: "reconcile",
                    scope: "selected",
                    orderIds: [confirm.row._id],
                  });
                } else if (confirm.kind === "override-one") {
                  runDecision({
                    action: "reconcile",
                    scope: "selected",
                    orderIds: [confirm.row._id],
                    override: true,
                  });
                } else if (confirm.kind === "reset-one") {
                  runDecision({
                    action: "reset",
                    scope: "selected",
                    orderIds: [confirm.row._id],
                  });
                }
              }}
              sx={{ textTransform: "none" }}
            >
              {repairProgress ? "Repairing…" : acting ? "Working…" : "Confirm"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default OrderChannelReview;
